import { useState, useCallback, createContext, useContext, ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import {CheckRoundIcon, ErrorRoundIcon} from "assets/icon";

interface ToastContextType {
    show: (options: { icon?: ReactNode; timeout?: number; text: ReactNode }) => void;
    success: (text: ReactNode, timeout?: number) => void;
    error: (text: ReactNode, timeout?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children,zIndex=299 }: { children: ReactNode,zIndex?:number }) {
    const [toasts, setToasts] = useState<{ id: number; icon?: ReactNode; text: ReactNode }[]>([]);

    const removeToast = useCallback((id: number) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const show = useCallback(({ icon, timeout = 3000, text }: { icon?: ReactNode; timeout?: number; text: ReactNode }) => {
        const id = Date.now()+Math.random();
        setToasts((prev) => [...prev, { id, icon, text }]);
        setTimeout(() => removeToast(id), timeout);
    }, [removeToast]);

    const success = useCallback((text: ReactNode, timeout?: number) => {
        show({ icon: <CheckRoundIcon color={"#2FCC00"} size={24}/>, text, timeout });
    }, [show]);

    const error = useCallback((text: ReactNode, timeout?: number) => {
        show({ icon: <ErrorRoundIcon size={24}/>, text, timeout });
    }, [show]);

    const ToastComponent = createPortal(
        <div className="matchid-toast-box" style={{
            zIndex
        }}>
            {toasts.reverse().map(({ id, icon, text }) => (
                <div
                    key={id}
                    className="matchid-toast"
                >
                    {icon && <span>{icon}</span>}
                    <span>{text}</span>
                </div>
            ))}
        </div>,
        document.body
    );

    return (
        <ToastContext.Provider value={{ show, success, error }}>
            {children}
            {ToastComponent}
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextType {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
}
